import Button from "@atlaskit/button/new";
import React, {ReactElement, useCallback, useEffect, useState} from "react";
import {useAsyncFn} from "react-use";
import {imageService} from "../../../api/ImageService";
import {ImageFileData, ImageType} from "./AdminImageModal";
import {useDropzone} from "react-dropzone";
import hintImgBackground from "../../../assets/img/Image-level1-empty.svg";
import hintImgTeaser from "../../../assets/img/Image-level2-empty.svg";
import hintImgBackgroundDark from "../../../assets/img/Image-level1-empty-dark.svg";
import hintImgTeaserDark from "../../../assets/img/Image-level2-empty-dark.svg";
import hintImgError from "../../../assets/img/image-resolve-error.svg";
import hintImgErrorDark from "../../../assets/img/image-resolve-error-dark.svg";
import classNames from "classnames";
import {MenuItemImageValidator} from "navigation-common/util/MenuItemImageValidator";

export interface Props {
    onImageChange: (data: ImageFileData) => void,
    onImageRemove: () => void,
    onCancel: () => void,
    imageType: ImageType,
    imageUrl?: string,
    menuKey: string,
}

export const ImageUploadForm = (props: Props) => {
    const [imageUrl, setImageUrl] = useState<string>(props.imageUrl || "");
    const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
    const [imageUploadErrorMsg, setImageUploadErrorMsg] = useState<string | undefined>();

    const [uploadImageState, uploadImage] = useAsyncFn(async (imageFile: File) => {
        try {
            const resizedImageFile = await imageService.downScaleImage(imageFile, 600);
            const result = await imageService.saveMenuItemImage(resizedImageFile, props.menuKey);
            props.onImageChange({downloadUrl: result.downloadUrl, imageStorageName: result.imageStorageName});
        } catch (e) {
            console.error("Error uploading image", e);
            setImageUploadErrorMsg("Error uploading image. Accept image formats are .jpg, .jpeg, .png. Image size should not exceed 5MB.");
        }
    }, [imageUploadErrorMsg]);

    const readImageFile = (imageFile: File) => {
        setImageUrl("");

        if (!RegExp(/image\/(png|jpeg)/).exec(imageFile.type)) {
            console.error("Invalid file type. Only .jpg, .jpeg, .png are accepted.");
            setImageUploadErrorMsg("Invalid file type. Only .jpg, .jpeg, .png are accepted.");
            return;
        }

        if (imageFile.size > MenuItemImageValidator.MAX_FILE_SIZE) {
            console.error("Image file is too large. Should not exceed 5MB.");
            setImageUploadErrorMsg("The selected image is too large. Please select an image that is smaller than 5MB.");
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            setImageUrl(e.target?.result as string);
        };
        reader.readAsDataURL(imageFile);

        setSelectedImageFile(imageFile);
        setImageUploadErrorMsg(undefined);
    }

    const removeImage = () => {
        setImageUrl("");
        setSelectedImageFile(null);
        setImageUploadErrorMsg(undefined);
    }

    const onConfirm = () => {
        if (selectedImageFile) {
            uploadImage(selectedImageFile);
        } else if (imageUrl) {
            props.onCancel();
        } else {
            props.onImageRemove();
        }
    }

    function showFileSelectError(error: FileSelectError) {
        if (error === FileSelectError.INVALID_FILE_TYPE) {
            setImageUploadErrorMsg("Invalid file type. Only .jpg, .jpeg, .png are accepted.");
        } else if (error === FileSelectError.FILE_TOO_LARGE) {
            setImageUploadErrorMsg("The selected image is too large. Please select an image that is smaller than 5MB.");
        }
    }

    return (
        <div className={"imageUploadForm"}>
            <div className={"image-preview-wrapper"}>
                <ImageUploadDropZone onFileRead={readImageFile} onFileReject={showFileSelectError}>
                    <>
                        {!imageUrl &&
                            <>
                                {imageUploadErrorMsg ?
                                    <div className={"hint-wrapper"}>
                                        <img src={hintImgError} className={"hint lightMode"} alt={"error"}/>
                                        <img src={hintImgErrorDark} className={"hint darkMode"} alt={"error"}/>
                                        <p className={"errorMessage"}>{imageUploadErrorMsg}</p>
                                    </div>
                                    :
                                    <div className={"hint-wrapper"}>
                                        <img src={props.imageType === ImageType.BACKGROUND ? hintImgBackground : hintImgTeaser} className={"hint lightMode"}
                                             alt={"hint"}/>
                                        <img src={props.imageType === ImageType.BACKGROUND ? hintImgBackgroundDark : hintImgTeaserDark}
                                             className={"hint darkMode"}
                                             alt={"hint"}/>
                                        <div>Drag and Drop an image here or <u>upload an image</u>.</div>
                                        <div className={"uploadConditions"}>Image format: .jpg, .png &#124; max file size: 5 MB</div>
                                    </div>
                                }
                            </>
                        }

                        {imageUrl && <img className={"image-preview"} alt={"image-preview"} src={imageUrl}/>}
                    </>
                </ImageUploadDropZone>
            </div>
            <div className={"actions"}>
                <Button appearance={"default"} onClick={removeImage} isDisabled={!imageUrl}>Remove</Button>
                <div style={{display: "flex", gap: "6px"}}>
                    <Button appearance={"subtle"} onClick={props.onCancel}>Cancel</Button>
                    <Button isDisabled={!!imageUploadErrorMsg} appearance={"primary"} isLoading={uploadImageState.loading} onClick={onConfirm}>Confirm</Button>
                </div>
            </div>
        </div>);
}

export const ImageUploadDropZone = (props: { onFileRead: (file: File) => void, onFileReject: (error: FileSelectError) => void, children: ReactElement }) => {
    const onDrop = useCallback((acceptedFiles: File[]) => {
        props.onFileRead(acceptedFiles[0]);
    }, [])
    const {getRootProps, getInputProps, isDragActive, isDragReject, fileRejections} = useDropzone({
        onDrop,
        accept: {type: MenuItemImageValidator.ACCEPTED_MIME_TYPES},
        maxSize: MenuItemImageValidator.MAX_FILE_SIZE,
        maxFiles: 1
    })

    useEffect(() => {
        if (fileRejections.length > 0) {
            fileRejections.forEach(({errors}) => {
                errors.forEach(({code}) => {
                    if (code === "file-invalid-type") {
                        props.onFileReject(FileSelectError.INVALID_FILE_TYPE);
                    } else if (code === "file-too-large") {
                        props.onFileReject(FileSelectError.FILE_TOO_LARGE);
                    }
                })
            })
        }
    }, [isDragReject]);

    return (
        <div {...getRootProps()} style={{height: "100%"}}>
            <input {...getInputProps()} />
            <div style={{height: "100%"}} className={classNames({"onDragOver": isDragActive})}>
                {props.children}
            </div>
        </div>
    )
};

enum FileSelectError {
    INVALID_FILE_TYPE = "INVALID_FILE_TYPE",
    FILE_TOO_LARGE = "FILE_TOO_LARGE",
}
